import * as React from 'react';
import { toast } from 'react-toastify';
import type { PriceListItem, PriceListTierList } from './models/AdminInterfaces';
import AddPriceListTier from './AdminPriceListsAddTierModal';
import Pricelist from './Pricelist';
import { useNavigate } from 'react-router-dom';
import type { ItemOptionList } from './models/JobSharedInterfaces';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';

interface PriceListDiscountItem {
    id: number;
    tierId: number;
    name: string;
    days: string;
    discount: string;
}

const AdminPriceListData = () => {
    let navigate = useNavigate();
    const [priceListTiers, setPriceListTiers] = React.useState<PriceListTierList[]>([]);
    const [itemList, setItemList] = React.useState<ItemOptionList[]>([]);
    const [priceListItems, setPriceListItems] = React.useState<PriceListItem[]>([]);
    const [priceListTier, setPriceListTier] = React.useState({ priceListUniqueId: "", priceListTierId: -1, priceListTierType: -1 });
    const [priceListDiscount, setPriceListDiscount] = React.useState<PriceListDiscountItem[]>([]);
    const [showAddTier, setShowAddTier] = React.useState(false);
    const [chargeCleaningFee, setChargeCleaningFee] = React.useState(true);
    const [chargeExtraEquipmentFee, setChargeExtraEquipmentFee] = React.useState(true);
    const [systemCompanyId, setSystemCompanyId] = React.useState(3);
    const [loading, setLoading] = React.useState(false);
    const [pricelistLoading, setPricelistLoading] = React.useState(false);
    const [readOnly, setReadOnly] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("AdminPricelistsView");
        if (hasPermission) {
            getPriceListData();
        } else {
            navigate("/accessdenied");
        }
    }

    const getPriceListData = async () => {
        const apiUrl = "GetPriceListData?IsEws=true&IsPumpClean=false&IsEpa=false&IsFencing=false&IsEvent=false";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setItemList(result.data.itemList);
            setPriceListTiers(result.data.priceListTier);
            setSystemCompanyId(result.data.systemCompanyId);
            setReadOnly(result.data.readOnly);
            setLoading(false);
        }
    }

    const getAdminPricelist = async (id: number, typeId: number) => {
        if (id > 0) {
            const apiUrl = "GetPriceList?Id=" + id + "&PriceListType=" + typeId;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var pricelist = result.data.priceList;
                let pricelistItem = addBlankRow(id, typeId * 1);
                pricelist.push(pricelistItem);
                setPriceListItems(pricelist);
                setPriceListDiscount(result.data.priceListDiscount);
                setChargeCleaningFee(result.data.chargeCleaningFee);
                setChargeExtraEquipmentFee(result.data.chargeExtraEquipmentFee);
                setPricelistLoading(false);
            }            
        } else {
            setPriceListItems([]);
            setPricelistLoading(false);
        }
    }

    const addBlankRow = (id: number, typeId: number) => {
        var tierId = -1;
        var customerId = -1;
        if (typeId === 1) {
            tierId = id;
            
        } else {
            customerId = id;
        }

        let pricelistItem: PriceListItem = {
            id: 0,
            tierId: tierId,
            customerId: customerId,
            customerName: "",
            itemId: -1,
            itemTypeId: 0,
            hirePeriod: 0,
            frequencyId: -1,
            locationId: -1,
            metreId: -1,
            baseDays: 0,
            basePrice: 0,
            baseMetres: 0,
            useBtm: false,
            metreRate: 0,
            travelAmount: 0,
            baseAdditionalHireRate: 0,
            additionalHireRate: 0,
            extraHourRate: 0,
            overhireRequired: true,
            statusId: 1,
            isDirty: false
        };

        return pricelistItem;
    }

    //ADD TIER
    const addTier = () => {
        setShowAddTier(true);
    }

    const hideAddTier = () => {
        setShowAddTier(false);
    }

    const createNewBlankPriceListItem = (id: number, typeId: number) => {
        let pricelist: PriceListItem[] = [];
        let pricelistItem = addBlankRow(id, typeId);
        pricelist.push(pricelistItem);
        setPriceListItems(pricelist);
    }

    const saveAddTier = (customerTierId: number, name: string, typeId: number) => {
        //need to add the customer to the dropdown
        var uniqueType = typeId === 1 ? 'T' : 'C';
        var uniqueId = uniqueType + customerTierId;
        var newItem: PriceListTierList = {
            id: customerTierId,
            uniqueId: uniqueId,
            name: name,
            typeId: typeId
        };
        setPriceListTiers(prevList => [...prevList, newItem]);
        setPriceListTier(prevState => ({ ...prevState, priceListUniqueId: uniqueId, priceListTierId: customerTierId, priceListTierType: typeId }));
        createNewBlankPriceListItem(customerTierId, typeId);
        setShowAddTier(false);
    }
    //END ADD TIER

    const handleChange = (e: any) => {
        var uniqueId = e.target.value;
        var plId = uniqueId === "" ? -1 : uniqueId.substring(1) * 1;
        let typeId = 1;

        if (uniqueId != "") {
            let priceListOption = priceListTiers[e.target.selectedIndex - 1];
            if (priceListOption) {
                typeId = priceListOption.typeId;
            }
        }

        setPriceListTier({ priceListUniqueId: uniqueId, priceListTierType: typeId, priceListTierId: plId });

        if (uniqueId != "") {
            getAdminPricelist(plId, typeId);
        }
    }

    const handleCleaningCheckboxChange = (e: any) => {
        updateChargeFees(e.target.checked, chargeExtraEquipmentFee);
    }

    const handleEquipmentCheckboxChange = (e: any) => {
        updateChargeFees(chargeCleaningFee, e.target.checked);
    }

    const updateChargeFees = async (cleaningFee: boolean, equipmentFee: boolean) => {
        var saveChargeFees = {
            id: priceListTier.priceListTierId,
            typeId: priceListTier.priceListTierType,
            cleaningFee: cleaningFee,
            extraEquipmentFee: equipmentFee,
            companyId: systemCompanyId
        };

        var jsonItem = JSON.stringify(saveChargeFees);

        const apiUrl = "SaveTierChargeFee";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Charge Fee Saved Successfully");
                setChargeCleaningFee(cleaningFee);
                setChargeExtraEquipmentFee(equipmentFee);
            }
        }
    }

    const updatePricelist = (updatedPricelist: PriceListItem[]) => {
        setPriceListItems(updatedPricelist);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (priceListItems.length === 1) {
            errorList["pricelistitems"] = "Please enter at least one item!";
            setErrors(errorList);
            return true;
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < priceListItems.length - 1; i++) {
            var priceListItem = priceListItems[i];
            rowNumber += 1;
            var newErrorString = "";

            if (priceListItem.itemId < 1) {
                newErrorString += "Item, ";
                error = true;
            }

            if (!priceListItem.basePrice || priceListItem.basePrice <= 0) {
                newErrorString += "Base Price, ";
                error = true;
            }

            if (!priceListItem.additionalHireRate || priceListItem.additionalHireRate <= 0) {
                newErrorString += "Additional Hire Rate, ";
                error = true;
            }

            if (systemCompanyId === 2 && (!priceListItem.extraHourRate || priceListItem.extraHourRate <= 0)) {
                newErrorString += "Extra Hour Rate, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
            
        }
        if (error) {
            formIsValid = false;
            errorList["pricelistitems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            var changed = [...priceListItems.filter(p => p.isDirty)];

            if (changed.length === 0) {
                toast.info("No items have changed.");
            } else {
                savePriceList(changed);
            }
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const savePriceList = async (changed: PriceListItem[]) => {
        var savePriceList = {
            priceListItems: changed
        };

        var jsonItem = JSON.stringify(savePriceList);

        const apiUrl = "SavePriceList";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Saved Successfully");
                setPricelistLoading(true);
                getAdminPricelist(priceListTier.priceListTierId, priceListTier.priceListTierType);
            }
        }
    }


    let addNewTier = <AddPriceListTier showModal={showAddTier} systemCompanyId={systemCompanyId} hideModal={hideAddTier} saveModal={saveAddTier} />
    let pricelist = <Pricelist systemCompanyId={systemCompanyId} id={priceListTier.priceListTierId} priceListTierTypeId={priceListTier.priceListTierType} pricelist={priceListItems} itemList={itemList} customerOptions={[]} frequencyList={[]} locationList={[]} metresList={[]} isEpa={false} isPumpClean={false} isFencing={false} isFencingCustom={false} isEvent={false} isPriceOnly={false} update={updatePricelist} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {addNewTier}
            </div>
            <div className="input-group-parent">
                <label className="input-group" htmlFor="priceListTierId">
                    <span className="label">Tier/Customer</span>
                    <select className="select marginRight10" id="priceListTierId" name="priceListTierId" value={priceListTier.priceListUniqueId} onChange={(e) => handleChange(e)}>
                        <option hidden defaultValue=""></option>
                        {priceListTiers.map(tier =>
                            <option key={tier.uniqueId} value={tier.uniqueId}>{tier.name}</option>
                        )};
                    </select>
                    <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__small"} type="button" onClick={addTier}>Add New</button>
                </label>
            </div>
            <div className={systemCompanyId == 2 ? "input-group-parent" : "hidden"}>
                <span>Extended hire period discount rates</span>
                <table className="pricelist-discount-width">
                    {priceListDiscount.map((item, index) => 
                        <tbody key={index}>
                            <tr>
                                <td>{item.name}</td>
                                <td>{item.days}</td>
                                <td>{item.discount}</td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
            <div className={systemCompanyId == 3 && priceListTier.priceListTierId > 0 ? "input-group-parent" : "hidden"}> 
                <label className="input-group" htmlFor="chargeCleaningFee">
                    <span className="label label-large label--nopadding">Charge Cleaning Fee</span>
                    <input className="checkbox" type="checkbox" id="chargeCleaningFee" name="chargeCleaningFee" checked={chargeCleaningFee} onChange={(e) => handleCleaningCheckboxChange(e)} disabled={readOnly}></input>
                </label>
                <label className="input-group" htmlFor="chargeExtraEquipmentFee">
                    <span className="label label-large label--nopadding">Charge Equipment (table & chairs) Fee</span>
                    <input className="checkbox" type="checkbox" id="chargeExtraEquipmentFee" name="chargeExtraEquipmentFee" checked={chargeExtraEquipmentFee} onChange={(e) => handleEquipmentCheckboxChange(e)} disabled={readOnly}></input>
                </label>
            </div>
            <div>
                <hr></hr>
            </div>
            <div className={priceListTier.priceListTierId != 0 && !pricelistLoading ? "" : "hidden"}>
                {pricelist}
                {errors["pricelistitems"] ?
                    (errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}
            </div>
            <div className={pricelistLoading ? "" : "hidden"}><p><em>Loading...</em></p></div>
            <div className="marginBottom10">
                <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__large"} type="button" onClick={save} disabled={pricelistLoading || priceListTier.priceListTierId <= 0 ? true : false}>Save</button>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;


    return <div>
        <h1>Price Lists</h1>
        {contents}
    </div>;

};

export default AdminPriceListData;